import React, { useState } from "react";
import { Button, IconButton } from "@mui/material";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { RiDeleteBinLine } from "react-icons/ri";
import useGetIngredients from "../../../hooks/catalog/ingredients/useGetIngredients";
import useDeleteIngredient from "../../../hooks/catalog/ingredients/useDeleteIngredient";
import Spinner from "../../../components/spinner";
import Errors from "../../errors";
import Pagination from "../../../components/elements/pagination";
import DeleteModal from "../../../components/elements/deleteModal";
import SwitchToggle from "../../../components/elements/switchtoggle/SwitchToggle";
import toast from "react-hot-toast";
import useCreate from "../../../hooks/useCreate";

const IngredientsList = () => {
  const {
    data,
    handlePageClick,
    handleSearchVal,
    paramsObject,
    handleSetLimit,
  } = useGetIngredients();

  const {
    ingredientId,
    setIngredientId,
    isDeleteModalOpen,
    setDeleteModalOpen,
    handleSubmit,
  } = useDeleteIngredient();

  const handleDelete = (id) => {
    setIngredientId(id);
    setDeleteModalOpen(true);
  };

  return (
    <div className="category px-4">
      <div className="category__head headingBorder__b">
        <h2 className="titleHeading">Ingredients</h2>
      </div>
      <div className="category_table py-5 space-y-5">
        {/* Controls Row */}
        <div className="flex flex-wrap items-end gap-4 mb-4">
          <div className="min-w-[21rem] md:w-auto md:flex-1">
            <input
              onChange={handleSearchVal}
              value={paramsObject.searchQuery}
              type="text"
              placeholder="Search By Key Word...."
              className="input"
            />
          </div>
          <div>
            <Link to={`add-ingredient`} className="block">
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                className="primaryBtn-outlined"
              >
                Add Ingredient
              </Button>
            </Link>
          </div>
          <div>
            <label htmlFor="" className="label">
              Limit
            </label>
            <select
              name="limit"
              value={data.data?.per_page || paramsObject.limit}
              className="input"
              onChange={handleSetLimit}
            >
              {Array(5)
                .fill(1)
                .map((_, idx) => {
                  return (
                    <option key={idx} value={(idx + 1) * 10}>
                      {(idx + 1) * 10}
                    </option>
                  );
                })}
            </select>
          </div>
        </div>

        {/* Table */}
        {data.isLoading || data.isFetching ? (
          <Spinner />
        ) : data.status === "error" ? (
          <Errors errorObject={data.error} inline />
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg shadow overflow-y-auto relative">
            <table className="border-collapse table-auto w-full whitespace-no-wrap bg-white table-striped relative">
              <thead>
                <tr className="text-left">
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                    Sr No
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                    Image
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                    Ingredient Name
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                    Status
                  </th>
                  <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.data?.data?.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center py-8 text-gray-500 font-medium"
                    >
                      No Data Found
                    </td>
                  </tr>
                ) : (
                  data.data?.data?.map((ingredientData, idx) => {
                    const { ingredient_id, name, media_url } =
                      ingredientData || {};
                    return (
                      <tr key={ingredient_id || idx}>
                        <td className="border-dashed border-t border-gray-200 px-6 py-2 text-sm">
                          {paramsObject.limit * paramsObject.pageNo + idx + 1}
                        </td>
                        <td className="border-dashed border-t border-gray-200 px-6 py-2 text-center">
                          {media_url ? (
                            <img
                              src={media_url}
                              alt={name}
                              className="h-12 w-12 object-cover rounded mx-auto"
                            />
                          ) : (
                            <div className="h-12 w-12 mx-auto flex items-center justify-center bg-gray-200 text-gray-500 text-xs font-medium rounded">
                              No Image
                            </div>
                          )}
                        </td>
                        <td className="border-dashed border-t border-gray-200 px-6 py-2 text-sm">
                          {name}
                        </td>

                        <td className="border-dashed border-t border-gray-200 px-6 py-2 text-sm">
                          <IngredientsTableStatusCell data={ingredientData} />
                        </td>

                        <td className="border-dashed border-t border-gray-200 px-6 py-2 text-center">
                          <div className="flex items-center justify-center">
                            <Link to={`edit-ingredient/${ingredient_id}`}>
                              <IconButton>
                                <EditIcon />
                              </IconButton>
                            </Link>
                            <div onClick={() => handleDelete(ingredient_id)}>
                              <IconButton>
                                <RiDeleteBinLine className="text-lg text-gray-600" />
                              </IconButton>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        <div>
          <Pagination
            currentPage={+paramsObject.pageNo}
            lengthofItems={data.data?.totalCount}
            limit={paramsObject.limit}
            onPageChange={handlePageClick}
            pageCount={Math.ceil(
              (data.data?.totalCount || 0) / (data.data?.limit || 10)
            )}
          />
        </div>
      </div>

      <DeleteModal
        open={isDeleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        title={"Delete Ingredient"}
        message={"Are you sure you want to delete this ingredient?"}
        onDelete={() => handleSubmit({ ingredient_id: ingredientId })}
      />
    </div>
  );
};

export default IngredientsList;

const IngredientsTableStatusCell = ({ data }) => {
  const [status, setStatus] = useState(data.status);

  const { setDataToServer: updateStatusReq } = useCreate({
    url: "ingredients/update_status",
    onSuccess: () => {
      toast.success("Category Status Updated Successfully", {
        position: "top-right",
        duration: 3000,
      });
    },
    onError: () => {
      toast.error("Failed to Update Category Status", {
        position: "top-right",
        duration: 3000,
      });
    },
  });

  const handleStatusChange = (e) => {
    const newStatus = e.target.checked;
    setStatus(newStatus);
    updateStatusReq.mutate({
      ingredient_id: data.ingredient_id,
      status: newStatus,
    });
  };

  return (
    <div className="flex items-center justify-center">
      <SwitchToggle
        label=""
        checkedValue={status}
        onChange={(e) => handleStatusChange(e)}
      />
    </div>
  );
};
